import { AcquisitionContext, PageContext, PageType, ShopperContext } from "./types";
import {
  getDismissedSlots,
  getFirstTouchAcquisition,
  getIntentProfile,
  getOrCreateId,
  getTriggerImpressionCounts,
  persistFirstTouchAcquisition,
} from "./storage";

export function collectShopperContext(shopId: string): ShopperContext {
  const context: ShopperContext = {
    shopId,
    anonymousId: getOrCreateId("anon"),
    sessionId: getSessionId(),
    acquisition: parseAcquisition(),
    page: parsePage(),
    seenSections: new Set(),
    dismissedSlotIds: getDismissedSlots(),
    triggerImpressionCounts: getTriggerImpressionCounts(),
    intentProfile: getIntentProfile(),
  };
  // Best-effort: fetch the real basket line-items (Shopify AJAX cart) and fold them in when they arrive.
  // The context object is shared, and a surface opens later (chip click / launcher), so by then this has
  // usually resolved; if not, the surface just falls back to the cart subtotal. Never throws.
  void fetchCartItems().then((items) => {
    if (items.length) context.cartItems = items;
  });
  return context;
}

/** Read the Shopify AJAX cart (/cart.js) into compact line-items. Best-effort — [] on any failure. */
export async function fetchCartItems(): Promise<Array<{ title: string; qty?: number }>> {
  try {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!res.ok) return [];
    const cart = (await res.json()) as { items?: Array<{ product_title?: string; title?: string; quantity?: number }> };
    return (cart.items ?? [])
      .map((i) => ({ title: String(i.product_title ?? i.title ?? "").trim(), ...(typeof i.quantity === "number" ? { qty: i.quantity } : {}) }))
      .filter((i) => i.title)
      .slice(0, 12);
  } catch {
    return [];
  }
}

function getSessionId(): string {
  const key = "gemsales:session";
  // Carry a mid-session shopper across the rename rather than splitting their session in two.
  const existing = sessionStorage.getItem(key) ?? sessionStorage.getItem("gemchat:session");
  if (existing) return existing;

  const value = `sess_${crypto.randomUUID()}`;
  sessionStorage.setItem(key, value);
  return value;
}

function parseAcquisition(): AcquisitionContext {
  const url = new URL(window.location.href);
  const currentTouch = {
    source: url.searchParams.get("utm_source"),
    medium: url.searchParams.get("utm_medium"),
    campaign: url.searchParams.get("utm_campaign"),
    content: url.searchParams.get("utm_content"),
    term: url.searchParams.get("utm_term"),
    referrer: document.referrer || null,
    landingPage: window.location.href,
  };
  const existingFirstTouch = getFirstTouchAcquisition();
  const firstTouch =
    existingFirstTouch ??
    {
      ...currentTouch,
      capturedAt: new Date().toISOString(),
    };

  if (!existingFirstTouch) {
    persistFirstTouchAcquisition(firstTouch);
  }

  return {
    ...currentTouch,
    firstTouch,
    isFirstTouch: !existingFirstTouch,
  };
}

function parsePage(): PageContext {
  const url = new URL(window.location.href);
  const path = url.pathname;

  return {
    url: url.href,
    path,
    type: inferPageType(path),
    productHandle: inferProductHandle(path),
    title: document.title,
    cartSubtotal: readCartSubtotal(),
  };
}

export function inferPageType(path: string): PageType {
  const normalized = path.toLowerCase();
  if (normalized === "/" || normalized === "") return "home";
  if (normalized.startsWith("/products/")) return "product";
  if (normalized.startsWith("/collections/")) return "collection";
  if (normalized.startsWith("/cart")) return "cart";
  if (isAdvertorialPath(normalized)) return "advertorial";
  if (normalized.startsWith("/blogs/")) return "article";
  return "unknown";
}

function isAdvertorialPath(path: string): boolean {
  return ["advertorial", "presale", "story", "guide", "landing", "fake-shilajit"].some((keyword) => path.includes(keyword));
}

function inferProductHandle(path: string): string | null {
  const match = path.match(/^\/products\/([^/?#]+)/);
  return match?.[1] ?? null;
}

function readCartSubtotal(): number | null {
  const candidates = [
    "[data-cart-subtotal]",
    "[data-cart-total]",
    ".cart-subtotal",
    ".cart__subtotal",
  ];

  for (const selector of candidates) {
    const element = document.querySelector<HTMLElement>(selector);
    const raw = element?.dataset.cartSubtotal ?? element?.dataset.cartTotal ?? element?.textContent;
    const value = raw ? Number(raw.replace(/[^0-9.]/g, "")) : Number.NaN;
    if (Number.isFinite(value)) return value;
  }

  return null;
}
